/**
 * Real-user performance monitoring (RUM).
 *
 * Measures Core Web Vitals on actual visitor devices and pushes every metric to
 * the GTM dataLayer as `web_vitals`. When a metric misses the budget in
 * scripts/perf/budgets.json (mobile and desktop budgets are separate) an extra
 * `perf_alert` event is pushed — wire that event to a GA4 custom alert or a
 * Slack/email notification in GTM to get told about slow homepage loads.
 *
 * Sent once per page view, on visibility change, using sendBeacon semantics
 * from the web-vitals library, so it costs the visitor nothing measurable.
 */
import { onCLS, onINP, onLCP, onTTFB, type Metric } from "web-vitals";
import { getDevice, trackEvent } from "@/lib/analytics";

type Budget = { lcpMs: number; clsScore: number; inpMs: number; ttfbMs: number };

const BUDGETS: Record<"mobile" | "desktop", Budget> = {
  mobile: { lcpMs: 3000, clsScore: 0.1, inpMs: 300, ttfbMs: 1500 },
  desktop: { lcpMs: 2000, clsScore: 0.1, inpMs: 200, ttfbMs: 1000 },
};

/** Routes we alert on aggressively — the money pages. */
const CRITICAL_PATHS = ["/", "/residential/panel-upgrades", "/contact"];

const budgetFor = (name: Metric["name"], device: "mobile" | "desktop") => {
  const b = BUDGETS[device];
  switch (name) {
    case "LCP":
      return b.lcpMs;
    case "INP":
      return b.inpMs;
    case "TTFB":
      return b.ttfbMs;
    case "CLS":
      return b.clsScore;
    default:
      return Number.POSITIVE_INFINITY;
  }
};

function report(metric: Metric) {
  const device = getDevice() === "mobile" ? "mobile" : "desktop";
  const budget = budgetFor(metric.name, device);
  const value = metric.name === "CLS" ? Number(metric.value.toFixed(4)) : Math.round(metric.value);
  const overBudget = value > budget;

  trackEvent("web_vitals", {
    metric_name: metric.name,
    metric_value: value,
    metric_rating: metric.rating,
    metric_budget: budget,
    over_budget: overBudget,
    device,
    connection:
      (navigator as Navigator & { connection?: { effectiveType?: string } }).connection
        ?.effectiveType ?? "unknown",
  });

  if (overBudget) {
    trackEvent("perf_alert", {
      metric_name: metric.name,
      metric_value: value,
      metric_budget: budget,
      metric_rating: metric.rating,
      device,
      critical_page: CRITICAL_PATHS.includes(window.location.pathname),
      severity: value > budget * 1.5 ? "high" : "warn",
    });
  }
}

let started = false;

export function initWebVitals() {
  if (started || typeof window === "undefined") return;
  started = true;
  onLCP(report);
  onCLS(report);
  onINP(report);
  onTTFB(report);
}
